import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { YesNoRow } from "../ui/yes-no-toggle";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Building, Search, Loader2 } from "lucide-react";
import DateDropdownPicker from "../ui/DateDropdownPicker";
import RequiredLabel from "../ui/RequiredLabel";
import { companyTypes, employeeCounts, contractTypes } from "../../utils/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployerInfoSection = ({ data, onChange, fieldErrors = {} }) => {
  const [cuiLoading, setCuiLoading] = useState(false);
  const [cuiError, setCuiError] = useState("");

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const errClass = (field) => fieldErrors[field] ? "border-red-500 ring-1 ring-red-500" : "";

  const [postalLoading, setPostalLoading] = useState(false);
  const [postalFindLoading, setPostalFindLoading] = useState(false);

  const handlePostalCodeChange = async (value) => {
    handleInputChange('empPostalCode', value);
    const clean = (value || "").replace(/\D/g, "");
    if (clean.length === 6) {
      setPostalLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/postal-lookup/${clean}`);
        if (res.ok) {
          const d = await res.json();
          onChange({ empPostalCode: clean, empCounty: d.county || "", empCity: d.city || "" });
        }
      } catch { /* ignore */ }
      setPostalLoading(false);
    }
  };

  const handleFindEmpPostalCode = async () => {
    const street = data.empStreet || "";
    const number = data.empNumber || "";
    const city = data.empCity || data.empCounty || "";
    if (!street || !city) return;
    setPostalFindLoading(true);
    try {
      const params = new URLSearchParams({ street, number, city });
      if (data.empCounty) params.append("county", data.empCounty);
      const res = await fetch(`${BACKEND_URL}/api/public/address-to-postal?${params}`);
      if (res.ok) {
        const r = await res.json();
        if (r.postalCode) {
          const updates = { empPostalCode: r.postalCode };
          if (r.county && !data.empCounty) updates.empCounty = r.county;
          if (r.city && !data.empCity) updates.empCity = r.city;
          onChange(updates);
        }
      }
    } catch { /* ignore */ }
    setPostalFindLoading(false);
  };

  const handleCUILookup = async () => {
    const cui = (data.cui || "").trim();
    if (!cui || cui.length < 2) {
      setCuiError("Introduceți un CUI valid");
      return;
    }

    setCuiLoading(true);
    setCuiError("");

    try {
      const cleanCUI = cui.replace(/\D/g, "");
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-cui/${cleanCUI}`);
      if (!response.ok) {
        setCuiError("CUI negăsit");
        setCuiLoading(false);
        return;
      }
      const result = await response.json();
      const src = result.data || result;

      // Auto-fill fields from ANAF data (don't lock them)
      const updates = {};
      if (src.denumire || src.company_name) updates.companyName = src.denumire || src.company_name;
      const addrVal = src.adresa || src.address;
      if (addrVal) {
        // Try to parse address into structured fields
        const addr = addrVal || "";
        updates.empAddrFull = addr; // store full address for reference
        // Simple parse: try to extract county/city from end
        const parts = addr.split(",").map(s => s.trim());
        if (parts.length >= 1) updates.empStreet = parts[0];
        if (parts.length >= 2) updates.empCity = parts[parts.length - 2] || "";
        if (parts.length >= 3) updates.empCounty = parts[parts.length - 1] || "";
      }
      if (src.telefon || src.phone) updates.companyPhone = src.telefon || src.phone;

      onChange(updates);
      setCuiError("");
    } catch {
      setCuiError("Eroare la verificare CUI");
    } finally {
      setCuiLoading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 border-b">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Building className="h-5 w-5" />
          Date Angajator
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CUI cu buton de căutare */}
          <div className="space-y-2">
            <RequiredLabel htmlFor="cui">CUI Companie</RequiredLabel>
            <div className="flex gap-2">
              <Input
                id="cui"
                data-testid="employer-cui"
                value={data.cui}
                onChange={(e) => handleInputChange('cui', e.target.value)}
                placeholder="Introduceți CUI-ul"
                className={`flex-1 ${errClass('cui')}`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCUILookup}
                disabled={cuiLoading}
                data-testid="cui-lookup-btn"
                className="px-3"
              >
                {cuiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {cuiError && <p className="text-xs text-red-500">{cuiError}</p>}
            <p className="text-xs text-gray-400">Apăsați lupa pentru a completa automat datele firmei</p>
          </div>

          {/* Nume companie */}
          <div className="space-y-2">
            <RequiredLabel htmlFor="companyName">Nume Companie</RequiredLabel>
            <Input
              id="companyName"
              data-testid="employer-companyName"
              value={data.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Numele companiei"
              className={errClass('companyName')}
            />
          </div>

          {/* Telefon companie */}
          <div className="space-y-2">
            <Label htmlFor="companyPhone">Telefon Companie</Label>
            <Input
              id="companyPhone"
              data-testid="employer-companyPhone"
              value={data.companyPhone || ""}
              onChange={(e) => handleInputChange('companyPhone', e.target.value)}
              placeholder="Telefon firmă (opțional)"
            />
          </div>

          {/* Tip companie */}
          <div className="space-y-2">
            <Label htmlFor="companyType">Tip Companie</Label>
            <Select
              value={data.companyType}
              onValueChange={(value) => handleInputChange('companyType', value)}
            >
              <SelectTrigger data-testid="employer-companyType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {companyTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="employeeCount">Număr Angajați</Label>
            <Select
              value={data.employeeCount}
              onValueChange={(value) => handleInputChange('employeeCount', value)}
            >
              <SelectTrigger data-testid="employer-employeeCount">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {employeeCounts.map(count => (
                  <SelectItem key={count} value={count}>{count}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Adresă Companie - structurată */}
        <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50/30">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Adresă Sediu Companie</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Județ / Sector</Label>
              <Input
                data-testid="emp-county"
                value={data.empCounty || ""}
                onChange={(e) => handleInputChange('empCounty', e.target.value)}
                placeholder="Ex: Sector 2 / Cluj"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Localitate</Label>
              <Input
                data-testid="emp-city"
                value={data.empCity || ""}
                onChange={(e) => handleInputChange('empCity', e.target.value)}
                placeholder="Ex: București"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Strada</Label>
              <Input
                data-testid="emp-street"
                value={data.empStreet || ""}
                onChange={(e) => handleInputChange('empStreet', e.target.value)}
                placeholder="Numele străzii"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nr.</Label>
              <Input
                data-testid="emp-number"
                value={data.empNumber || ""}
                onChange={(e) => handleInputChange('empNumber', e.target.value)}
                placeholder="Nr."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bloc</Label>
              <Input
                data-testid="emp-block"
                value={data.empBlock || ""}
                onChange={(e) => handleInputChange('empBlock', e.target.value)}
                placeholder="Bloc"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scara</Label>
              <Input value={data.empStaircase || ""} onChange={(e) => handleInputChange('empStaircase', e.target.value)} placeholder="Sc." />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Etaj</Label>
              <Input value={data.empFloor || ""} onChange={(e) => handleInputChange('empFloor', e.target.value)} placeholder="Et." />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Apartament</Label>
              <Input value={data.empApartment || ""} onChange={(e) => handleInputChange('empApartment', e.target.value)} placeholder="Ap." />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cod Poștal</Label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <Input value={data.empPostalCode || ""} onChange={(e) => handlePostalCodeChange(e.target.value)} placeholder="Cod" maxLength={6} />
                  {postalLoading && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-blue-500" />}
                </div>
                <button
                  type="button"
                  onClick={handleFindEmpPostalCode}
                  disabled={postalFindLoading || (!data.empStreet && !data.empCity)}
                  className="px-2 h-9 border border-blue-300 rounded bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 text-xs font-medium shrink-0"
                >
                  {postalFindLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Caută"}
                </button>
              </div>
              <p className="text-xs text-blue-500">Completați strada + localitate apoi apăsați "Caută"</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Contract */}
          <div className="space-y-2">
            <Label htmlFor="contractType">Tip Contract</Label>
            <Select
              value={data.contractType}
              onValueChange={(value) => handleInputChange('contractType', value)}
            >
              <SelectTrigger data-testid="employer-contractType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {contractTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contractPeriod">Perioada Contract</Label>
            <Input
              id="contractPeriod"
              value={data.contractPeriod}
              onChange={(e) => handleInputChange('contractPeriod', e.target.value)}
              placeholder="Ex: 2 ani, nedeterminată"
            />
          </div>

          {/* Data Angajării - Dropdown Zi/Luna/An */}
          <div className="space-y-2">
            <RequiredLabel>Data Angajării</RequiredLabel>
            <DateDropdownPicker
              id="hireDate"
              value={data.hireDate}
              onChange={(val) => handleInputChange('hireDate', val)}
              hasError={!!fieldErrors.hireDate}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="totalSeniority">Vechime Totală (ani)</RequiredLabel>
            <Input
              id="totalSeniority"
              type="number"
              value={data.totalSeniority}
              onChange={(e) => handleInputChange('totalSeniority', e.target.value)}
              placeholder="Vechimea în muncă"
              step="0.5"
              className={errClass('totalSeniority')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="profession">Profesia</RequiredLabel>
            <Input
              id="profession"
              value={data.profession}
              onChange={(e) => handleInputChange('profession', e.target.value)}
              placeholder="Profesia exercitată"
              className={errClass('profession')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="currentPosition">Funcția Actuală</RequiredLabel>
            <Input
              id="currentPosition"
              value={data.currentPosition}
              onChange={(e) => handleInputChange('currentPosition', e.target.value)}
              placeholder="Funcția ocupată în prezent"
              className={errClass('currentPosition')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="caenCode">Cod CAEN</RequiredLabel>
            <Input
              id="caenCode"
              value={data.caenCode}
              onChange={(e) => handleInputChange('caenCode', e.target.value)}
              placeholder="Codul de activitate economică"
              className={errClass('caenCode')}
            />
          </div>
        </div>

        {/* Întreruperi */}
        <div className="mt-6 space-y-4">
          <YesNoRow
            label="Au existat concedii/întreruperi de muncă în ultimii 2 ani?"
            value={data.hasInterruptions ?? null}
            onChange={(v) => handleInputChange('hasInterruptions', v)}
            testid="has-interruptions"
          />

          {data.hasInterruptions === true && (
            <div className="space-y-2">
              <Label htmlFor="interruptions">Detalii Întreruperi</Label>
              <Textarea
                id="interruptions"
                value={data.interruptions}
                onChange={(e) => handleInputChange('interruptions', e.target.value)}
                placeholder="Descrieți tipul și durata întreruperilor"
                rows={3}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EmployerInfoSection;
